import { Button } from '@/components/ui/button'
import Updating from '@/components/ui/Updating'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Component1Icon,
  Component2Icon,
  DownloadIcon,
  EraserIcon,
  LayersIcon,
  MagicWandIcon,
  ResetIcon,
  UploadIcon,
} from '@radix-ui/react-icons'
import { postFetcher } from 'api/swr'
import { useEditorStore } from 'CanvasCore/uiSlice'
import { useConfigStore } from 'AdminPanel/Spine/store/configStore'
import { useEditorStore as useSpineEditorStore } from 'AdminPanel/Spine/store/editorStore'
import { relatedSelectionHasDynamicSpine } from 'AdminPanel/Spine/lib/hasDynamicSpineSelection'
import { DEFAULT_VAP_ENCODE_LIMITS } from 'AdminPanel/Spine/editor/export/components/export-vap/vapConfig'
import { VapEncodeSettings } from 'AdminPanel/Spine/editor/create-avatar/VapEncodeSettings'
import { ExportAnimationProgressDialog } from 'AdminPanel/Spine/editor/export/components/export-animation/ExportAnimationProgressDialog'
import { ImageUploadProgressDialog } from 'AdminPanel/Spine/editor/create-avatar/ImageUploadProgressDialog'
import { resetAvatarEditorPreview } from 'Editor/lib/avatarSpinePreview'
import { useUI } from 'Editor'
import { displayedPickerCards } from 'Editor/lib/visibleComponents'
import { captureCanvasAvatarPayload } from 'Editor/utils/captureCanvasAvatarPayload'
import { normalizeStaticFullscreenType } from 'Editor/utils/fullscreenType'
import { createMixedSpineAvatar } from 'Editor/utils/createMixedSpineAvatar'
import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import useSWR from 'swr'
import { Random } from './Random'
import { toast } from '@/components/ui/use-toast'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'

function downloadURI(uri, name) {
  var link = document.createElement('a')
  link.download = name
  link.href = uri
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const Overlay = ({
  drawRef,
  charDrawRef,
  spineSelectionActive,
  fullscreenType,
}) => {
  const { tid } = useParams()
  const stage = useEditorStore((state) => state.editors[tid]?.stage)
  const avatarSpineExport = useUI((s) => s.avatarSpineExport)

  const loadedRelatedTemplates = useConfigStore((s) => s.loadedRelatedTemplates)
  const allRelatedTemplateIds = useConfigStore((s) => s.allRelatedTemplateIds)
  const selectedSkins = useSpineEditorStore((s) => s.selectedSkins)
  const selectedToningColors = useSpineEditorStore((s) => s.selectedToningColors)
  const currentAnimation = useSpineEditorStore((s) => s.currentAnimation)

  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [desc, setDesc] = useState('')
  const [encodeLimits, setEncodeLimits] = useState({
    ...DEFAULT_VAP_ENCODE_LIMITS,
  })
  const [isSaving, setIsSaving] = useState(false)
  const [exportProgressOpen, setExportProgressOpen] = useState(false)
  const [exportProgress, setExportProgress] = useState(0)
  const [uploadProgressOpen, setUploadProgressOpen] = useState(false)

  const resetForm = () => {
    setName('')
    setDesc('')
    setEncodeLimits({ ...DEFAULT_VAP_ENCODE_LIMITS })
  }

  const handleDialogOpenChange = (nextOpen) => {
    if (isSaving) return
    setOpen(nextOpen)
    if (!nextOpen) resetForm()
  }

  const sortedRelatedTemplates = useMemo(() => {
    const orderIndex = new Map()
    ;(allRelatedTemplateIds || []).forEach((id, i) => orderIndex.set(id, i))
    return [...(loadedRelatedTemplates || [])].sort(
      (a, b) => (orderIndex.get(a.id) ?? 0) - (orderIndex.get(b.id) ?? 0),
    )
  }, [loadedRelatedTemplates, allRelatedTemplateIds])

  const hasDynamicSpine = useMemo(
    () => relatedSelectionHasDynamicSpine(selectedSkins, loadedRelatedTemplates),
    [selectedSkins, loadedRelatedTemplates],
  )

  // 普通头像上传（无关联 Spine）：数据与上传接口不变，仅新增可选 name/desc
  const runNormalUpload = async () => {
    const captured = captureCanvasAvatarPayload(
      tid,
      stage,
      drawRef,
      fullscreenType,
    )
    if (!captured) {
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: 'Cannot capture canvas',
      })
      return
    }
    if ('error' in captured && captured.error === 'draft') {
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: 'Cannot upload draft layer',
      })
      return
    }
    const { drawData } = captured

    setIsSaving(true)
    try {
      await postFetcher('/avatars', {
        arg: {
          avatar: `${tid}:${Date.now()}`,
          tid,
          drawData,
          fullscreenType: normalizeStaticFullscreenType(fullscreenType),
          name: name.trim() || undefined,
          desc: desc.trim() || undefined,
        },
      })
      toast({
        title: 'Avatar 上传发起成功',
        className: 'bg-green-500 text-white',
      })
      setOpen(false)
      resetForm()
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Avatar 上传失败',
        description: err?.message || '请稍后重试',
      })
    } finally {
      setIsSaving(false)
    }
  }

  // 混合头像上传（结合关联 Spine）：有动态编 VAP，仅静态则上传 webp
  const runMixedUpload = async () => {
    if (!tid || !stage?.h) return

    setIsSaving(true)
    setExportProgressOpen(true)
    setExportProgress(0)
    try {
      const { mode } = await createMixedSpineAvatar({
        tid,
        stage,
        drawRef,
        charDrawRef,
        relatedTemplates: sortedRelatedTemplates,
        selectedSkins,
        selectedToningColors,
        currentAnimation,
        avatarSpineExport,
        encodeLimits,
        name,
        desc,
        fullscreenType,
        onExportProgress: setExportProgress,
        onUploadStart: () => {
          setExportProgressOpen(false)
          setExportProgress(0)
          setUploadProgressOpen(true)
        },
      })

      toast({
        title: 'Avatar 上传发起成功',
        description:
          mode === 'image'
            ? '静态混合封面已上传，worker 将完成落库'
            : '混合 Spine 视频已上传，worker 将生成封面并落库',
        className: 'bg-green-500 text-white',
      })
      setOpen(false)
      resetForm()
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Avatar 上传失败',
        description: err?.message || '请稍后重试',
      })
    } finally {
      setExportProgressOpen(false)
      setExportProgress(0)
      setUploadProgressOpen(false)
      setIsSaving(false)
    }
  }

  const handleSave = () => {
    if (spineSelectionActive) {
      void runMixedUpload()
    } else {
      void runNormalUpload()
    }
  }

  const handleExport = () => {
    const captured = captureCanvasAvatarPayload(
      tid,
      stage,
      drawRef,
      fullscreenType,
      { allowDraft: true },
    )
    if (!captured || 'error' in captured) {
      toast({
        variant: 'destructive',
        title: '下载失败',
        description: 'Cannot capture canvas',
      })
      return
    }
    downloadURI(captured.avatar, 'avatar.png')
  }

  return (
    <>
      <Info />
      <div className="absolute top-0 right-0 flex items-center gap-2">
        <Button variant="outline" size="icon" onClick={handleExport}>
          <DownloadIcon className="w-4 h-4" />
        </Button>
        <Button variant="outline" onClick={() => setOpen(true)} disabled={isSaving}>
          {isSaving ? (
            <Updating />
          ) : (
            <>
              <UploadIcon className="w-4 h-4 mr-2" />
              Avatar
            </>
          )}
        </Button>
      </div>

      <ExportAnimationProgressDialog
        open={exportProgressOpen}
        format="webp"
        progress={exportProgress}
        title={
          hasDynamicSpine
            ? '正在生成 VAP 视频（512 + 304）'
            : '正在合成静态图'
        }
      />
      <ImageUploadProgressDialog open={uploadProgressOpen} />

      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>上传 Avatar</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="avatar-name">名称</Label>
              <Input
                id="avatar-name"
                placeholder="请输入 Avatar 名称（可选）"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="avatar-desc">描述</Label>
              <Textarea
                id="avatar-desc"
                placeholder="请输入 Avatar 描述（可选）"
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                disabled={isSaving}
                rows={4}
              />
            </div>

            {spineSelectionActive && hasDynamicSpine ? (
              <VapEncodeSettings
                value={encodeLimits}
                onChange={setEncodeLimits}
                disabled={isSaving}
              />
            ) : spineSelectionActive ? (
              <p className="text-sm text-muted-foreground">
                当前仅静态关联组件，将上传 webp 图片，不生成视频。
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleDialogOpenChange(false)}
              disabled={isSaving}
            >
              取消
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? '保存中...' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="absolute bottom-0 left-0 w-full flex items-center gap-2">
        <ToggleButton />
        <div className="ml-auto flex items-center gap-2">
          <ResetButton />
          <Random />
        </div>
      </div>
    </>
  )
}

const ResetButton = () => {
  const { tid } = useParams()
  return (
    <Button
      variant="outline"
      size="icon"
      onClick={() => {
        if (tid) resetAvatarEditorPreview(tid)
      }}
      title="清空画布与关联 Spine 预览"
    >
      <EraserIcon className="h-4 w-4" />
    </Button>
  )
}

const StatHint = ({ icon, children, label }) => (
  <HoverCard openDelay={120} closeDelay={80}>
    <HoverCardTrigger asChild>
      <div className="flex cursor-help items-center">{icon}{children}</div>
    </HoverCardTrigger>
    <HoverCardContent
      side="right"
      align="center"
      className="w-auto px-2 py-1 text-xs"
    >
      {label}
    </HoverCardContent>
  </HoverCard>
)

const Info = () => {
  const { tid } = useParams()
  const { data: cpts } = useSWR(['/components', { tid }])
  const { data: dlcs } = useSWR(['/dlcs', { tid }])
  const drawerCptLength = useEditorStore(
    (state) => state.editors[tid]?.drawers?.length,
  )
  const { data: layers } = useSWR(['/layers', { tid }])
  const liveCpts = useMemo(
    () => displayedPickerCards(cpts, tid, dlcs, undefined, layers),
    [cpts, tid, dlcs, layers],
  )
  const aiCpts = liveCpts.filter((cpt) => cpt.ai === 1)
  const aiCptCount = useEditorStore(
    (state) =>
      state.editors[tid]?.drawers?.filter((drawer) => drawer.ai === 1)?.length,
  )

  const drawerLayerCount = useEditorStore(
    (state) =>
      Array.from(new Set(state.editors[tid]?.drawers?.map((d) => d.lid)))
        .length,
  )
  const proCpts = useMemo(
    () => liveCpts.filter((cpt) => (cpt.pro ?? 0) > 0),
    [liveCpts],
  )

  return (
    <div className="absolute top-0 left-0 flex flex-col">
      <StatHint
        icon={<Component1Icon className="w-4 h-4 mr-2" />}
        label="组件"
      >
        {drawerCptLength || 0}/{liveCpts.length}
      </StatHint>
      <StatHint
        icon={<Component2Icon className="w-4 h-4 mr-2" />}
        label="AI组件"
      >
        {aiCptCount || 0}/{aiCpts.length || 0}
      </StatHint>
      <StatHint
        icon={<LayersIcon className="w-4 h-4 mr-2" />}
        label="层"
      >
        {drawerLayerCount || 0}/{layers?.length || 0}
      </StatHint>
      <StatHint
        icon={<span className="mr-2 w-4 text-xs font-semibold">Pro</span>}
        label="Pro组件"
      >
        {proCpts.length}/{liveCpts.length}
      </StatHint>
    </div>
  )
}

const ToggleButton = () => {
  const currTab = useUI((state) => state.currTab)
  const toggleTab = useUI((state) => state.toggleTab)
  if (currTab === 'tonings')
    return (
      <Button variant="outline" size="icon" onClick={() => toggleTab('layers')}>
        <ResetIcon className="h-4 w-4" />
      </Button>
    )
  return (
    <Button variant="outline" size="icon" onClick={() => toggleTab('tonings')}>
      <MagicWandIcon className="h-4 w-4" />
    </Button>
  )
}

export default Overlay
